import { useCallback, useMemo, useState } from "react";
import { AspectRatio, Box, Flex, Text, Badge, Button, Skeleton } from "@/ui";
import { motion } from "motion/react";
import VerticalDots from "assets/icons/vertical_dots.svg?react";
import ActivityLog from "assets/icons/history_log.svg?react";
import CameraOff from "assets/icons/camera_off.svg?react";
import Camera from "assets/icons/camera.svg?react";
import SettingsOutlined from "assets/icons/settings_outlined.svg?react";
import MoveToTop from "assets/icons/moveToTop.svg?react";
import Circle from "@/components/Circle";
import IconButton from "@/components/IconButton";
import Menu, { MenuProps } from "@/components/Menu";
import Icon from "@/components/Icon";
import { absoluteCenter, textOverflowDots } from "@/theme/utils/style";
import { CameraColors, CameraIcons } from "@/enums/camera";
import { t } from "@/utils";
import { useTransparentize } from "@/theme/utils/transparentize";
import { usePresenceKeeper } from "@/modules/app/Presence";
import { isStationOffline } from "@/utils/isStationOffline";
import { useStation } from "viewer/store/station";
import LastActivity from "./LastActivity";
import ImageWithBlurredBg from "./ImageWithBlurredBg";
import BatteryIndicator from "./BatteryIndicator";

type Props = {
  camera: StoreCameraDevice;
  preview: CameraPreviewState[string];
  isFirstInList: boolean;
  openCameraHistory: () => void;
  handleConnect: () => void;
  openSettings: () => void;
  changeCameraStationRecordingMode: (mode: CameraStationRecordingMode) => Promise<boolean>;
};

export default function CameraPreview({
  camera,
  preview,
  isFirstInList,
  handleConnect,
  openSettings,
  openCameraHistory,
  changeCameraStationRecordingMode
}: Props) {
  const circleBorder = useTransparentize("neutral.99", 0.2);
  const presences = usePresenceKeeper();
  const [isChangingRecordingMode, setIsChangingRecordingMode] = useState(false);
  const moveCameraToTop = useStation((s) => s.moveCameraToTop);
  const presence = presences[camera.jid];

  const isTurnedOff = presence === "sender_off";
  const isAvailable = !isStationOffline(presence) && presence !== "not_chosen_yet" && !isTurnedOff;
  const isOffline = isStationOffline(presence) || presence === "not_chosen_yet";
  const isCharging = preview?.status?.batteryStatus?.state === "CHARGING";
  const hasBatteryStatus = preview?.status?.batteryStatus?.state !== "UNKNOWN" && preview?.status?.batteryStatus?.level;
  const doesSupportStop = camera.supportedFeatures?.includes("CAMERA_STATION_STOP_RECORDING");

  const changeRecordingMode = useCallback(
    async (mode: CameraStationRecordingMode) => {
      setIsChangingRecordingMode(true);
      await changeCameraStationRecordingMode(mode);
      setIsChangingRecordingMode(false);
    },
    [changeCameraStationRecordingMode]
  );

  const items = useMemo<MenuProps["items"]>(
    () => [
      {
        item: t("event.history"),
        props: {
          onClick: openCameraHistory,
          icon: <Icon icon={ActivityLog} fill="on-surface-variant" />
        }
      },
      ...(!isFirstInList
        ? [
            {
              item: t("camera.moveToTop"),
              props: {
                onClick: () => moveCameraToTop(camera.jid),
                icon: <Icon icon={MoveToTop} fill="on-surface-variant" />
              }
            }
          ]
        : []),
      ...(doesSupportStop && !isOffline
        ? [
            {
              item: isTurnedOff ? t("startCamera") : t("pauseCamera"),
              props: {
                onClick: isTurnedOff ? () => changeRecordingMode("ON") : () => changeRecordingMode("OFF"),
                icon: <Icon size="24px" icon={isTurnedOff ? Camera : CameraOff} fill="on-surface-variant" />
              }
            }
          ]
        : []),
      {
        item: t("camera.settings"),
        props: {
          onClick: openSettings,
          icon: <Icon icon={SettingsOutlined} fill="on-surface-variant" />
        }
      }
    ],
    [
      camera.jid,
      changeRecordingMode,
      doesSupportStop,
      isOffline,
      isTurnedOff,
      isFirstInList,
      moveCameraToTop,
      openCameraHistory,
      openSettings
    ]
  );

  return (
    <Box maxW={["100%", "calc(50% - 12px)"]} flexBasis={["100%", "50%"]}>
      <Box
        w="100%"
        h="100%"
        as={motion.div}
        layoutId={`camera-view-${camera.jid}`}
        bg="surface-variant"
        borderTopRadius="md"
        borderBottomRadius="md"
        overflow="hidden"
      >
        <Box
          position="relative"
          bg="neutral-variant.20"
          borderRadius="md"
          tabIndex={isAvailable ? 0 : -1}
          onClick={handleConnect}
          _hover={{ cursor: "pointer" }}
          _focusVisible={{ outline: "2px solid", outlineColor: "primary" }}
          pointerEvents={isAvailable ? "auto" : "none"}
          overflow="hidden"
        >
          {(!preview?.image || !isAvailable) && (
            <Flex
              {...absoluteCenter}
              color="white"
              zIndex={2}
              flexDir="column"
              align="center"
              justify="center"
              textAlign="center"
              h="100%"
              w="100%"
            >
              {((!preview?.image && isAvailable) || isChangingRecordingMode) && (
                <Skeleton h="100%" w="100%" opacity={1} startColor="neutral-variant.20" endColor="neutral-variant.40" />
              )}
              {!isChangingRecordingMode && (
                <>
                  {!isAvailable && <h4>{isTurnedOff ? t("cameraIsPaused") : t("camera.offline")}</h4>}
                  {isTurnedOff && (
                    <Button
                      pointerEvents={isChangingRecordingMode ? "none" : "auto"}
                      mt="1rem"
                      variant="outlineFixedMedium"
                      color="primary.80"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        changeRecordingMode("ON");
                      }}
                    >
                      {t("startCamera")}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          )}
          {isAvailable && !isCharging && hasBatteryStatus && (
            <BatteryIndicator batteryLevel={preview!.status!.batteryStatus!.level} gradient />
          )}
          {isAvailable && (
            <Badge variant="status" pos="absolute" right="12px" top="12px" zIndex="1">
              {t("online").toUpperCase()}
            </Badge>
          )}
          <AspectRatio ratio={16 / 9}>
            {preview?.image?.url ? (
              <ImageWithBlurredBg
                displayForegroundImage={isAvailable}
                src={preview.image.url}
                containerProps={
                  isAvailable
                    ? {}
                    : {
                        _after: {
                          content: "''",
                          bg: "neutral-variant.20",
                          opacity: 0.6,
                          pos: "absolute",
                          inset: 0
                        }
                      }
                }
              />
            ) : (
              <></>
            )}
          </AspectRatio>
        </Box>
        <Flex p="15px" align="center" justify="space-between">
          <Flex maxW="calc(100% - 36px)" align="center" columnGap="12px">
            <Circle bg="surface" props={{ border: "1px solid", borderColor: circleBorder }}>
              {<Icon icon={CameraIcons[camera.icon].icon} fill={CameraColors[camera.color].value} />}
            </Circle>
            <Box overflow="hidden">
              <Text as="h6" color="on-surface" {...textOverflowDots}>
                {camera.name}
              </Text>
              {isAvailable && <LastActivity preview={preview} />}
            </Box>
          </Flex>
          <Menu items={items} menuProps={{ computePositionOnMount: true }}>
            <IconButton
              icon={VerticalDots}
              fill="on-surface-variant"
              as="span"
              bg="surface-variant"
              tooltip={{ label: t("moreOptions") }}
            />
          </Menu>
        </Flex>
      </Box>
    </Box>
  );
}
